import { Cell, createColumnHelper } from "@tanstack/react-table";
import merge from "lodash/merge";
import { Fragment } from "react";

import { CountingProgressBar } from "@/components/CountingProgressBar";
import { SOSEntityHashLink } from "@/components/SosEntityHashLink";
import { SOSEntityLineItemHashLink } from "@/components/SosEntityLineItemHashLink";
import { PriorityOption } from "@/features/purchasing-priority/purchasing-priority-utils";
import { getCountingBarSortFunction } from "@/utils/react-table-utils";
import { QuantitiesForPriority } from "../../queries/tracking-data/bom-by-component";
import { ItemReceipt } from "../../queries/tracking-data/item-receipts";
import { PurchaseOrder } from "../../queries/tracking-data/purchase-orders";
import { ExistingInventoryQuantityTableCell } from "../existing-inventory/ExistingInventoryQuantityTableCell";
import { JobTrackingInternalLink } from "../JobTrackingInternalLink";
import { PreferredVendorCell } from "../PreferredVendorCell";
import { QuantityRequiredForJobCell } from "../QuantityRequiredForJobCell";
import { ComponentPurchasingProgress } from "./purchasing-progress-data";
import { PurchasingNote } from "./PurchasingNoteCell";
import { getPurchasingNoteColumnDef } from "./purchasing-note-column";
import { QuantityWithUOMAbbr } from "@/components/QuantityWithUOMAbbr";
import { LeadTimeCell } from "../LeadTimeCell";

export interface PurchasingProgressTableRow extends ComponentPurchasingProgress {
  jobId: number;
  rowId: string;

  quantitiesForSelectedPriority: QuantitiesForPriority;

  /**
   * The initial note to display when the row is first loaded. Undefined indicates that the initial state is still
   * loading, null indicates that there is no note for this item/job combination.
   */
  initialNote: PurchasingNote | Error | null | undefined;
}

// Define a callback that fades out all cells for non-inventory items
const purchasingProgressCellProps = (
  cell: Cell<PurchasingProgressTableRow, unknown>,
  { ...additionalProps }: Omit<React.TdHTMLAttributes<HTMLTableCellElement>, "rowSpan"> = {},
) => {
  const isNonInventory = cell.row.original.component.item.type === "Non-inventory Item";
  return {
    ...additionalProps,
    className: `${isNonInventory ? "text-body-tertiary" : ""}${additionalProps.className ?? ""}`,
  };
};

// Create helpers for defining columns
const columnHelper = createColumnHelper<PurchasingProgressTableRow>();
const accessorCol: typeof columnHelper.accessor = (accessor, column) =>
  columnHelper.accessor(accessor, merge({ meta: { cellProps: purchasingProgressCellProps } }, column));

const itemDetailColumns = [
  accessorCol("component.item.name", {
    header: "Item",
    enableHiding: false,
    cell: ({ row }) => (
      <JobTrackingInternalLink trackerPath="bom-by-component">
        {({ targetPath, targetPageTitle }) => (
          <SOSEntityHashLink
            fromHash={row.id}
            urlPath={targetPath}
            tooltipContent={`View on '${targetPageTitle}' page`}
            type="item"
            id={row.original.component.item.id}
            label={row.original.component.item.name}
          />
        )}
      </JobTrackingInternalLink>
    ),
  }),
  accessorCol("component.item.type", {
    header: "Type",
    enableHiding: false,
    sortingFn: (a, b) => a.original.sortIndexForType - b.original.sortIndexForType,
  }),
  accessorCol("component.item.description", { header: "Description" }),
  accessorCol((x) => x.component.item.preferredVendor?.name, {
    id: "component_item_preferredVendor_name",
    header: "Preferred Vendor",
    cell: ({ row }) => <PreferredVendorCell item={row.original.component.item} />,
  }),
  columnHelper.accessor("component.item.leadTimeMs", {
    header: "Lead Time",
    cell: ({ row }) => <LeadTimeCell item={row.original.component.item} />,
  }),
];

const purchasingProgressColumns = [
  accessorCol("quantitiesForSelectedPriority.quantityRequiredForJob", {
    header: "Total Required",
    cell: ({ table, row }) => (
      <QuantityRequiredForJobCell
        item={row.original.component.item}
        priority={table.options.meta!["priority"] as PriorityOption}
      />
    ),
    meta: {
      headerCellProps: { className: "border-start-double" },
      cellProps: (cell) => purchasingProgressCellProps(cell, { className: "border-start-double text-end" }),
    },
  }),
  accessorCol("quantitiesForSelectedPriority.existingInventoryQuantity", {
    header: "# From Inventory",
    cell: ({ row }) => <ExistingInventoryQuantityTableCell item={row.original.component.item} fromRowId={row.id} />,
    meta: { cellProps: (cell) => purchasingProgressCellProps(cell, { className: "text-end" }) },
  }),
  accessorCol("quantitiesForSelectedPriority.nonInventoryQuantity", {
    header: "# Required To Purchase",
    cell: ({ getValue, row }) => (
      <QuantityWithUOMAbbr quantity={getValue()} uom={row.original.component.item.baseItemUOM?.uom} />
    ),
    meta: { cellProps: (cell) => purchasingProgressCellProps(cell, { className: "text-end" }) },
  }),
  accessorCol("numOrdered", {
    header: "# Ordered",
    sortingFn: getCountingBarSortFunction<PurchasingProgressTableRow>(
      (row) => row.numOrdered,
      (row) => row.quantitiesForSelectedPriority.nonInventoryQuantity,
    ),
    cell: ({ getValue, row }) => (
      <CountingProgressBar
        now={getValue()}
        max={row.original.quantitiesForSelectedPriority.nonInventoryQuantity}
        selectedItemUOM={row.original.component.item.baseItemUOM}
        baseUOM={row.original.component.item.baseItemUOM?.uom}
      />
    ),
  }),
  accessorCol("numReceived", {
    header: "# Received",
    cell: ({ getValue, row }) =>
      row.original.numOrdered ?
        <CountingProgressBar
          now={getValue()}
          max={row.original.numOrdered}
          selectedItemUOM={row.original.component.item.baseItemUOM}
          baseUOM={row.original.component.item.baseItemUOM?.uom}
        />
      : <span className="text-body-tertiary">N/A</span>,
  }),
  accessorCol("numReceivedToSoRack", {
    header: "# Received To SO Rack",
    cell: ({ getValue, row }) =>
      row.original.numReceived ?
        <CountingProgressBar
          now={getValue()}
          max={row.original.numReceived}
          selectedItemUOM={row.original.component.item.baseItemUOM}
          baseUOM={row.original.component.item.baseItemUOM?.uom}
        />
      : <span className="text-body-tertiary">N/A</span>,
  }),
  accessorCol("quantitiesByPo", {
    header: "Purchase Orders",
    enableSorting: false,
    cell: ({ row, getValue }) => <QuantitiesByPoTableCell rowId={row.id} quantitiesByPo={getValue()} />,
  }),
  accessorCol("quantitiesByReceipt", {
    header: "Item Receipts",
    enableSorting: false,
    cell: ({ row, getValue }) => <QuantitiesByReceiptTableCell rowId={row.id} quantitiesByReceipt={getValue()} />,
  }),
];

// Export the full set of columns for the table
// NOTE: We have to define the group columns separately before this because the type inference for the group columns is broken in Tanstack Table
// See: https://github.com/TanStack/table/issues/5065
export const purchasingProgressTableColumns = [
  columnHelper.group({ header: "Item Details", columns: itemDetailColumns }),
  columnHelper.group({
    header: "Purchasing Progress",
    columns: purchasingProgressColumns,
    meta: { headerCellProps: { className: "border-start-double" } },
  }),
  getPurchasingNoteColumnDef<PurchasingProgressTableRow>({
    getItemId: (row) => row.component.item.id,
  }),
];

const QuantitiesByPoTableCell: React.FC<{
  rowId: string;
  quantitiesByPo: Map<PurchaseOrder, number>;
}> = ({ rowId, quantitiesByPo }) => (
  <pre>
    {[...quantitiesByPo.entries()].map(([po, quantity], index) => (
      <Fragment key={index}>
        {index > 0 && <br />}
        {quantity.toLocaleString(undefined, { maximumFractionDigits: 5 })} x {po.starEmoji}
        <JobTrackingInternalLink trackerPath="purchase-orders">
          {({ targetPath, targetPageTitle }) => (
            <SOSEntityLineItemHashLink
              fromHash={rowId}
              urlPath={targetPath}
              tooltipContent={`View on '${targetPageTitle}' page`}
              type="purchaseorder"
              id={po.id}
              lineNumber={0}
              label={po.number}
            />
          )}
        </JobTrackingInternalLink>
      </Fragment>
    ))}
  </pre>
);

const QuantitiesByReceiptTableCell: React.FC<{
  rowId: string;
  quantitiesByReceipt: Map<ItemReceipt, number>;
}> = ({ rowId, quantitiesByReceipt }) => (
  <pre>
    {[...quantitiesByReceipt.entries()].map(([receipt, quantity], index) => (
      <Fragment key={index}>
        {index > 0 && <br />}
        {quantity.toLocaleString(undefined, { maximumFractionDigits: 5 })} x{" "}
        <JobTrackingInternalLink trackerPath="landed-costs">
          {({ targetPath, targetPageTitle }) => (
            <SOSEntityHashLink
              fromHash={rowId}
              urlPath={targetPath}
              tooltipContent={`View on '${targetPageTitle}' page`}
              type="itemreceipt"
              id={receipt.id}
              label={receipt.number}
            />
          )}
        </JobTrackingInternalLink>
      </Fragment>
    ))}
  </pre>
);
